import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, listMyAmendments } from "../lib/api";
import { userIdentity } from "../lib/identity";
import { useCountryConfig } from "../lib/useCountryConfig";
import { ArrowLeft, Loader2, CheckCircle2, Circle, Siren, MapPin, RefreshCcw, Plus, Camera, MessageSquare, MapPinned, ListChecks, Mic, Video as VideoIcon, Image as ImageIcon } from "lucide-react";
import { AmendmentModal } from "../components/AmendmentModal";

// Linear progress through the public statuses. `under_review` is the
// "catch-all" stage that absorbs `suspect` and `rejected_false` so a
// citizen never sees their alert was disbelieved.
const STAGES = ["received", "under_review", "in_process", "help_on_the_way", "resolved"];

const STAGE_META = {
  received:        { label: "Reçue",            tagline: "Votre alerte est enregistrée." },
  under_review:    { label: "En examen",        tagline: "Une équipe étudie votre alerte." },
  in_process:      { label: "En traitement",    tagline: "Les autorités traitent la situation." },
  help_on_the_way: { label: "Secours assignés", tagline: "Les secours ont été affectés." },
  resolved:        { label: "Résolue",          tagline: "Votre alerte est clôturée. Merci." },
  closed:          { label: "Clôturée",         tagline: "Votre alerte est clôturée." },
};

const fmtTs = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (_e) { return iso; }
};

const AMEND_META = {
  evidence_added:                { icon: Camera,         label: "Preuve ajoutée" },
  photo_added:                   { icon: ImageIcon,      label: "Photo ajoutée" },
  audio_added:                   { icon: Mic,            label: "Audio ajouté" },
  video_added:                   { icon: VideoIcon,      label: "Vidéo ajoutée" },
  description_added:             { icon: MessageSquare,  label: "Information ajoutée" },
  description_corrected:         { icon: MessageSquare,  label: "Description corrigée" },
  citizen_note_added:            { icon: MessageSquare,  label: "Note ajoutée" },
  location_clarification_added:  { icon: MapPinned,      label: "Lieu précisé" },
  category_correction_requested: { icon: ListChecks,     label: "Catégorie à corriger" },
};

export default function AlertStatusPage() {
  const { alertId } = useParams();
  const navigate = useNavigate();
  const cfg = useCountryConfig();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [amendments, setAmendments] = useState([]);
  const [showAmendModal, setShowAmendModal] = useState(false);
  const [alertCategory, setAlertCategory] = useState(null);

  const load = useCallback(async () => {
    const phone = userIdentity.getPhone();
    if (!phone) {
      setError("Impossible d'identifier votre numéro. Réouvrez l'application depuis l'accueil.");
      setLoading(false);
      return;
    }
    try {
      const { data: resp } = await api.get(`/alerts/${alertId}/status`, { params: { reporter_phone: phone } });
      setData(resp);
      setError(null);
      // Pull the citizen's own amendment timeline + category in parallel.
      try {
        const am = await listMyAmendments(alertId, phone);
        setAmendments(am.items || []);
      } catch (_e) { /* non-fatal */ }
      try {
        const { data: alert } = await api.get(`/alerts/${alertId}`);
        setAlertCategory(alert?.emergency_type || null);
      } catch (_e) { /* non-fatal */ }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) setError("Cette alerte est introuvable ou ne vous appartient pas.");
      else setError("Impossible de charger le statut. Réessayez.");
    } finally { setLoading(false); }
  }, [alertId]);

  useEffect(() => {
    let mounted = true;
    // Wrap in a runner that bails if the component unmounted so a slow
    // request can never call setState after teardown.
    const run = async () => { if (mounted) await load(); };
    run();
    // Re-poll every 20 s as a safety-net. Real-time updates ride the
    // citizen WebSocket below (Iter65); the slow poll catches missed
    // events and corrects any drift.
    const id = setInterval(run, 20_000);
    return () => { mounted = false; clearInterval(id); };
  }, [load]);

  // Iter65 — real-time citizen WebSocket. The backend pushes
  // `alert.status_changed` events into the citizen room keyed by their
  // reporter phone whenever the assigned agency updates the alert.
  // Payload is citizen-safe: no internal notes, no internal labels —
  // only the simplified user_facing_status the UI already understands.
  useEffect(() => {
    const phone = userIdentity.getPhone();
    if (!phone) return undefined;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const wsBase = base.replace(/^http/i, "ws");
    let ws;
    let closedByUs = false;
    let retry = 0;
    let timer = null;
    const open = () => {
      try { ws = new WebSocket(`${wsBase}/api/ws/citizen/${encodeURIComponent(phone)}`); }
      catch (_e) { schedule(); return; }
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); }
        catch { return; }
        if (!msg || !msg.type) return;
        // Only react to events for THIS alert.
        if (msg.alert_id !== alertId) return;
        if (msg.type === "alert.status_changed") {
          setData((prev) => prev ? {
            ...prev,
            user_facing_status: msg.user_facing_status,
            status_updated_at: msg.status_updated_at,
          } : prev);
          load();
        } else if (msg.type === "alert.citizen_amended" && msg.amendment) {
          // Prepend (server timestamps are sorted ascending so we
          // append to the end of the existing list).
          setAmendments((prev) => {
            const seen = new Set(prev.map((x) => x.id));
            if (seen.has(msg.amendment.id)) return prev;
            return [...prev, msg.amendment];
          });
        }
      };
      ws.onclose = () => {
        if (!closedByUs) schedule();
      };
      ws.onerror = () => { try { ws.close(); } catch (_e) { /* noop */ } };
    };
    const schedule = () => {
      const delay = Math.min(1000 * Math.pow(2, retry), 30000);
      retry += 1;
      timer = setTimeout(open, delay);
    };
    open();
    return () => {
      closedByUs = true;
      if (timer) clearTimeout(timer);
      if (ws) { try { ws.close(); } catch (_e) { /* noop */ } }
    };
  }, [alertId, load]);

  const currentStage = data?.user_facing_status || "received";
  // Resolution short-circuits the linear path; `closed` is the absolute end.
  const isClosed = currentStage === "closed";
  const reachedIndex = isClosed ? STAGES.length : STAGES.indexOf(currentStage);

  return (
    <div className="min-h-[100dvh] bg-rescue-bg flex flex-col" data-testid="alert-status-page">
      <header className="px-5 pt-5 pb-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="alert-status-back"
          className="p-2 -ml-2 text-rescue-ink hover:text-mali-red"
          aria-label="Retour"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="min-w-0">
          <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-rescue-mute" data-testid="alert-status-brand">{cfg.brand_name} · Suivi</div>
          <h1 className="font-display text-2xl font-black tracking-tight leading-none mt-0.5">Statut de votre alerte</h1>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="alert-status-refresh"
          className="ml-auto p-2 text-rescue-mute hover:text-rescue-ink"
          aria-label="Rafraîchir"
        >
          <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </header>

      <div className="px-5 pb-8">
        {loading && !data && (
          <div className="py-12 text-center text-sm text-rescue-mute flex flex-col items-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin" /> Chargement…
          </div>
        )}
        {error && (
          <div className="border-2 border-rose-300 bg-rose-50 text-rose-800 px-3 py-3 text-sm" data-testid="alert-status-error">
            {error}
            <button type="button" onClick={load} className="block mt-2 text-rose-900 underline font-bold text-xs">
              Réessayer
            </button>
          </div>
        )}
        {data && (
          <>
            {/* Current stage hero */}
            <div className="border-2 border-rescue-ink bg-white p-4" data-testid="alert-status-current">
              <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-mali-red">Étape actuelle</div>
              <div className="font-display text-3xl font-black mt-1 tracking-tight">{STAGE_META[currentStage]?.label || currentStage}</div>
              <p className="text-sm text-rescue-ink mt-2">{STAGE_META[currentStage]?.tagline}</p>
              {data.status_updated_at && (
                <div className="font-mono text-[10px] text-rescue-mute mt-3">
                  Mis à jour · {fmtTs(data.status_updated_at)}
                </div>
              )}
            </div>

            {/* Linear progress indicator */}
            <div className="mt-5 border-2 border-rescue-line bg-white p-4" data-testid="alert-status-progress">
              <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-rescue-mute mb-3">Progression</div>
              <ol className="space-y-3">
                {STAGES.map((stage, idx) => {
                  const reached = idx <= reachedIndex;
                  const active = idx === reachedIndex && !isClosed;
                  return (
                    <li key={stage} className="flex items-start gap-3" data-testid={`alert-status-stage-${stage}`}>
                      <div className={`shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center ${
                        reached ? "border-mali-red bg-mali-red text-white" : "border-rescue-line text-rescue-mute"
                      }`}>
                        {reached ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                      </div>
                      <div className={`pt-0.5 ${active ? "font-bold" : ""}`}>
                        <div className={`text-sm ${reached ? "text-rescue-ink" : "text-rescue-mute"} ${active ? "font-black" : ""}`}>
                          {STAGE_META[stage].label}
                          {active && <span className="ml-1.5 text-mali-red text-[10px] uppercase tracking-[0.2em]">· en cours</span>}
                        </div>
                        <div className="text-[11px] text-rescue-mute">{STAGE_META[stage].tagline}</div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>

            {/* Timeline of user-facing messages */}
            <div className="mt-5 border-2 border-rescue-line bg-white p-4" data-testid="alert-status-timeline">
              <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-rescue-mute mb-3">Chronologie</div>
              {(data.history || []).length === 0 ? (
                <div className="text-sm text-rescue-mute">Pas encore d&apos;événement.</div>
              ) : (
                <ol className="space-y-3">
                  {(data.history || []).map((h, idx) => (
                    <li key={h.id || idx} className="border-l-2 border-mali-red pl-3 py-0.5" data-testid={`alert-status-timeline-row-${idx}`}>
                      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-mali-red">
                        {STAGE_META[h.user_facing_status]?.label || h.user_facing_status}
                        {h.is_reopen && <span className="ml-2 text-cyan-700">· Réouverture</span>}
                      </div>
                      {h.user_message && (
                        <div className="text-sm text-rescue-ink mt-0.5">{h.user_message}</div>
                      )}
                      <div className="font-mono text-[10px] text-rescue-mute mt-0.5">{fmtTs(h.created_at)}</div>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            {/* Iter66 — Citizen amendments: "Compléter / corriger" button +
                timeline of submitted amendments. */}
            <div className="mt-5 border-2 border-rescue-line bg-white p-4" data-testid="alert-status-amend-section">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-rescue-mute">
                  Compléter / corriger
                </div>
                <button
                  type="button"
                  onClick={() => setShowAmendModal(true)}
                  data-testid="alert-status-amend-btn"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border-2 border-rescue-ink bg-rescue-ink text-white text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-mali-red hover:border-mali-red transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Ajouter une information
                </button>
              </div>
              <p className="text-[11px] text-rescue-mute mb-3">
                Avez-vous oublié quelque chose ? Vous pouvez compléter ou corriger sans toucher à votre alerte d&apos;origine.
              </p>
              {amendments.length === 0 ? (
                <div className="text-xs text-rescue-mute italic">Aucune mise à jour de votre part pour l&apos;instant.</div>
              ) : (
                <ol className="space-y-3" data-testid="alert-status-amend-list">
                  {amendments.map((a, idx) => {
                    const meta = AMEND_META[a.change_type] || AMEND_META.citizen_note_added;
                    const Icon = meta.icon;
                    return (
                      <li
                        key={a.id || idx}
                        className="border-l-2 border-cyan-500 pl-3 py-0.5"
                        data-testid={`alert-status-amend-row-${idx}`}
                      >
                        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-cyan-700 inline-flex items-center gap-1.5">
                          <Icon className="w-3 h-3" /> {meta.label}
                        </div>
                        {a.note && <div className="text-sm text-rescue-ink mt-0.5">{a.note}</div>}
                        {a.location_clarification && (
                          <div className="text-sm text-rescue-ink mt-0.5"><MapPinned className="w-3 h-3 inline mr-1" />{a.location_clarification}</div>
                        )}
                        {a.requested_category && (
                          <div className="text-sm text-rescue-ink mt-0.5">→ {a.requested_category}</div>
                        )}
                        {(a.attachments || []).length > 0 && (
                          <div className="text-[11px] text-rescue-mute mt-1">
                            {a.attachments.length} pièce(s) jointe(s) ajoutée(s)
                          </div>
                        )}
                        <div className="font-mono text-[10px] text-rescue-mute mt-0.5">{fmtTs(a.created_at)}</div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            <Link
              to="/"
              className="mt-6 block w-full text-center border-2 border-rescue-ink bg-white text-rescue-ink py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-rescue-ink hover:text-white transition-colors"
              data-testid="alert-status-home-link"
            >
              <Siren className="w-4 h-4 inline mr-1.5 align-text-bottom" /> Retour à l&apos;accueil
            </Link>
          </>
        )}
      </div>
      <AmendmentModal
        alertId={alertId}
        open={showAmendModal}
        onClose={() => setShowAmendModal(false)}
        onSubmitted={(am) => setAmendments((prev) => {
          // Dedup against the realtime WS push (alert.citizen_amended)
          // which may append the same amendment id — same guard as the
          // WS handler above.
          const seen = new Set(prev.map((x) => x.id));
          if (seen.has(am.id)) return prev;
          return [...prev, am];
        })}
        currentCategory={alertCategory}
      />
    </div>
  );
}
